import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { User } from '../models/models.lib';
import { ApiService } from '../api/api.service';
import { SettingsService } from '../settings/settings.service';
import { LoggerService } from '../shared/logger.service';
import { Settings } from '../common/obj.lib';

const { apiUrl } = environment;

export abstract class CasiUserService extends BehaviorSubject<Array<any>> {
  public loading: boolean = false;
  private settings: Settings = this.settingsService.getSettings();

  constructor(
    private http: HttpClient,
    private settingsService: SettingsService
  ) {
    super([]);
  }

  public query(): void {
    this.fetch()
      .subscribe((x) => {
        console.log('query: x: ', x);
        return super.next(x);
      });
  }

  protected fetch(): Observable<User[]> {
    this.loading = true;
    this.settings = this.settingsService.getSettings();

    return this.http.get(encodeURI(`${this.settings.apiUrl}/users`))
      .pipe(
        map((response: any, i) => <User[]>response),
        map(r => r.map(x => User.create(x))),
        // tap(nr => {
        //   console.log('nr: ', nr);
        // }),
        tap(() => (this.loading = false))
      )
  }
}

@Injectable({
  providedIn: 'root'
})
export class UserDataService extends CasiUserService {

  constructor(
    http: HttpClient,
    private settingService: SettingsService,
    private apiService: ApiService,
    private logger: LoggerService
  ) {
    super(http, settingService);
  }

  //#region Private Methods

  setActive(userId: number, active: boolean): Promise<any> {
    active = !active;
    return this.apiService.postHttpCallAsPromise('useractive', { userId, active }, 'setActive');
  }

  deleteUser(userId: number): Promise<any> {
    return this.apiService.deleteHttpCallAsPromise(`users/${userId}`, 'deleteUser');
  }

  getUsers(): Promise<User[]> {
    return this.apiService.getHttpCallAsPromise('users', 'getUsers')
      .then(data => {
        if (data && Array.isArray(data)) {
          return data.map(d => User.create(d));
        }
        return [];
      });
  }

  getRoles(): Promise<any> {
    return this.apiService.getHttpCallAsPromise('findroles', 'getRoles');
  }

  getUserRoles(userId: number): Promise<any> {
    return this.apiService.getHttpCallAsPromise(`findanyuserroles/${userId}`, 'getUserRoles');
  }

  updateUser(user: any, userRoles: any, userLocations?: any, supervisorId?: number): Promise<any> {
    return this.apiService.postHttpCallAsPromise('updateuser', {
      user,
      userRoles,
      userLocations,
      supervisorId
    }, 'updateUser');
  }

  createUser(user: any, userRoles: any, userLocations?: any, supervisorId?: number): Promise<any> {
    return this.apiService.postHttpCallAsPromise('user', {
      user,
      userRoles,
      userLocations,
      supervisorId
    }, 'createUser');
  }

  getUserByEmail(email: string): Promise<any> {
    return this.apiService.postHttpCallAsPromise('finduserbyemail', { email }, 'getUserByEmail');
  }
  checkEmailExists(email: string): Promise<any> {
    return this.apiService.postHttpCallAsPromise('checkemailexists', { email }, 'getUserByEmail');
  }

  updateUserProfile(user: any): Promise<any> {
    return this.apiService.putHttpCallAsPromise(`userprofile/${user.uuid}`, user, 'updateUserProfile');
  }

  updatePassword(user: any): Promise<any> {
    return this.apiService.postHttpCallAsPromise('updatepassword', user, 'updatePassword');
  }

  checkPassword(email: string, resettoken: string) {
    return this.apiService.postHttpCallAsPromise('checkpassword', {
      email,
      resettoken
    }, 'checkPassword');
  }

  signUp(user: any): Promise<any> {
    return this.apiService.postHttpCallAsPromise('signup/', user, 'signUp');
  }

  resetPassword(accessToken: string, password: string): Promise<any> {
    this.logger.debug(`resetPassword: `, accessToken, password);
    return this.apiService.postHttpCallAsPromise('passwordreset/', {
      accessToken,
      password
    }, 'resetPassword');
  }

  sendResetRequest(email: string): Promise<any> {
    return this.apiService.postHttpCallAsPromise('email/sendresetlink/', {
      email
    }, 'sendResetRequest');
  }

  requestSMSToken(email: string): Promise<any> {
    return this.apiService.postHttpCallAsPromise(`user/requestsmstoken`, {
      email
    })
  };

  requestAccessToken(email: string, code: string): Promise<any> {
    return this.apiService.postHttpCallAsPromise(`user/checksmstoken`, {
      email, code
    })
  };

  //#endregion

}
